import { useLayoutEffect, useState } from "react";
import { MdInfoOutline } from "react-icons/md";
import {
  Alert,
  Button,
  Chip,
  Drawer,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { WORK_ORDER_BUNDLE_STATUS } from "constants/index";
import { typographyStyles } from "constants/styles";
import { useSession } from "providers/SessionProvider";
import type {
  IMovementWorkOrderBundleUpdate,
  IMovementWorkOrderBundleDetail,
  IMovementWorkOrderFractionationAmounts,
  WorkOrderOriginalStatus,
} from "types";

type Draft = { packages: string; loose: string; scrap: string };
type BundleDraft = { available: Draft; not_available?: Draft };

interface Props {
  open: boolean;
  bundle: IMovementWorkOrderBundleDetail | null;
  busy: boolean;
  onCancel: () => void;
  onSave: (values: IMovementWorkOrderBundleUpdate) => void;
}

const toDraft = (group: IMovementWorkOrderFractionationAmounts): Draft => ({
  packages: String(group.packages),
  loose: String(group.loose),
  scrap: String(group.scrap),
});

const getPackageSize = (bundle: IMovementWorkOrderBundleDetail) => {
  const size = bundle.article.equivalence?.items_per_package;
  return typeof size === "number" && Number.isSafeInteger(size) && size > 0
    ? size
    : null;
};

const toBundleDraft = (
  bundle: IMovementWorkOrderBundleDetail,
): BundleDraft => {
  const packageSize = getPackageSize(bundle);
  const packages = packageSize
    ? Math.floor(bundle.available.amount / packageSize) * packageSize
    : 0;
  return {
    available: !bundle.completed && packageSize
      ? {
          packages: String(packages),
          loose: String(bundle.available.amount - packages),
          scrap: "0",
        }
      : toDraft(bundle.available),
    ...(bundle.not_available
      ? {
          not_available: bundle.completed
            ? toDraft(bundle.not_available)
            : {
                packages: "0",
                loose: "0",
                scrap: String(bundle.not_available.amount),
              },
        }
      : {}),
  };
};

const parseAmount = (value: string) =>
  value.trim() !== "" && /^\d+$/.test(value) ? Number(value) : NaN;

const parseDraft = (draft: Draft): IMovementWorkOrderFractionationAmounts => ({
  packages: parseAmount(draft.packages),
  loose: parseAmount(draft.loose),
  scrap: parseAmount(draft.scrap),
});

const validGroup = (
  values: IMovementWorkOrderFractionationAmounts,
  amount: number,
  packageSize: number | null,
) =>
  [values.packages, values.loose, values.scrap].every(
    (value) => Number.isSafeInteger(value) && value >= 0,
  ) && values.packages + values.loose + values.scrap === amount &&
  (!packageSize || values.packages % packageSize === 0);

export default function FractionationBundleEditor({
  open,
  bundle,
  busy,
  onCancel,
  onSave,
}: Props) {
  const { stores, locations, sublocations } = useSession();
  const [draft, setDraft] = useState<BundleDraft | null>(() =>
    bundle ? toBundleDraft(bundle) : null,
  );
  const [storeId, setStoreId] = useState<number | null>(null);
  const [parentLocationId, setParentLocationId] = useState<number | null>(null);
  const [subLocationId, setSubLocationId] = useState<number | null>(null);

  // Reset before paint when reopening the same bundle; its key does not change.
  useLayoutEffect(() => {
    if (open && bundle) {
      setDraft(toBundleDraft(bundle));
      const selectedId = bundle.location_id ?? bundle.source_location_id;
      const selectedSub = sublocations.find((location) => location.id === selectedId);
      setStoreId(bundle.store_id ?? bundle.source_store_id);
      setParentLocationId(selectedSub?.parent_location_id ?? selectedId);
      setSubLocationId(selectedSub?.id ?? null);
    }
  }, [open, bundle, sublocations]);

  if (!bundle || !draft) return null;

  const readOnly = bundle.completed;
  const parentLocations = locations.filter((location) =>
    location.store_id === storeId && !location.parent_location_id &&
    (!location.is_hidden || (readOnly && location.id === parentLocationId)),
  );
  const availableSubLocations = sublocations.filter((location) =>
    location.parent_location_id === parentLocationId &&
    location.store_id === storeId &&
    ((location.consolidated_at == null && !location.is_hidden) ||
      (readOnly && location.id === subLocationId)),
  );
  const destinationLocationId = availableSubLocations.length
    ? subLocationId : parentLocationId;
  const packageSize = getPackageSize(bundle);
  const taken = bundle.status === WORK_ORDER_BUNDLE_STATUS.IN_PROCESS;
  const values: IMovementWorkOrderBundleUpdate = {
    store_id: storeId ?? 0,
    location_id: destinationLocationId ?? 0,
    available: parseDraft(draft.available),
    ...(draft.not_available
      ? { not_available: parseDraft(draft.not_available) }
      : {}),
  };
  const valid =
    !!storeId && !!destinationLocationId &&
    parentLocations.some((location) => location.id === parentLocationId) &&
    (!availableSubLocations.length ||
      availableSubLocations.some((location) => location.id === subLocationId)) &&
    validGroup(values.available, bundle.available.amount, packageSize) &&
    (bundle.not_available
      ? !!values.not_available &&
        validGroup(values.not_available, bundle.not_available.amount, packageSize)
      : !values.not_available);
  const changeDraft = (
    originalStatus: WorkOrderOriginalStatus,
    field: keyof Draft,
    value: string,
  ) => {
    if (!open || busy || readOnly) return;
    if (packageSize && field === "scrap") return;
    setDraft((current) => {
      const currentGroup = current?.[originalStatus];
      if (!current || !currentGroup) return current;
      const nextGroup: Draft = { ...currentGroup, [field]: value };
      if (packageSize) {
        const packages = parseAmount(nextGroup.packages);
        const loose = parseAmount(nextGroup.loose);
        const amount = originalStatus === "available"
          ? bundle.available.amount
          : (bundle.not_available?.amount ?? 0);
        const remainder = amount - packages - loose;
        nextGroup.scrap = Number.isSafeInteger(remainder) && remainder >= 0
          ? String(remainder)
          : "";
      }
      return { ...current, [originalStatus]: nextGroup };
    });
  };

  const renderEditorGroup = (originalStatus: WorkOrderOriginalStatus) => {
    const groupDraft = draft[originalStatus];
    const groupValues = values[originalStatus];
    if (!groupDraft || !groupValues) return null;
    const amount =
      originalStatus === "available"
        ? bundle.available.amount
        : (bundle.not_available?.amount ?? 0);
    const label =
      originalStatus === "available" ? "Disponibles" : "No disponibles";
    const assigned =
      groupValues.packages + groupValues.loose + groupValues.scrap;
    const invalidPackageMultiple = !!packageSize &&
      Number.isSafeInteger(groupValues.packages) &&
      groupValues.packages % packageSize !== 0;
    return (
      <Stack
        key={originalStatus}
        spacing={2}
        sx={
          originalStatus === "not_available"
            ? { pt: 1.5, borderTop: 1, borderColor: "divider" }
            : undefined
        }
      >
        <Typography
          fontWeight={700}
          color={
            originalStatus === "not_available" ? "warning.main" : "text.primary"
          }
        >
          {label}: {amount} {amount === 1 ? "unidad" : "unidades"}
        </Typography>
        {packageSize && !readOnly && amount > 0 && (
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ color: "text.secondary" }}>
            <MdInfoOutline aria-hidden="true" size={20} style={{ flexShrink: 0, marginTop: 2 }} />
            <Typography variant="body2" color="inherit">
              Cada paquete contiene {packageSize} unidades. Las unidades en paquetes deben ser múltiplo de {packageSize}; scrap se calcula con el resto.
            </Typography>
          </Stack>
        )}
        {originalStatus === "not_available" && amount > 0 && !readOnly && (
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ color: "text.secondary" }}>
            <MdInfoOutline aria-hidden="true" size={20} style={{ flexShrink: 0, marginTop: 2 }} />
            <Typography variant="body2" color="inherit">
              Sugerencia: registrar las {amount} unidades no disponibles como scrap.
              Podés modificar estas cantidades antes de guardar.
            </Typography>
          </Stack>
        )}
        {amount > 0 &&
          (["packages", "loose", "scrap"] as const).map((field) => {
            const fieldLabel =
              field === "packages"
                ? "Unidades en paquetes"
                : field === "loose"
                  ? "Unidades sueltas"
                  : "Unidades scrap";
            return (
              <TextField
                key={field}
                fullWidth
                type="number"
                label={
                  bundle.not_available
                    ? `${fieldLabel} ${label.toLowerCase()}`
                    : fieldLabel
                }
                value={groupDraft[field]}
                inputProps={{
                  min: 0,
                  step: field === "packages" && packageSize ? packageSize : 1,
                  inputMode: "numeric",
                }}
                InputProps={{ readOnly: !!packageSize && field === "scrap" }}
                error={field === "packages" && invalidPackageMultiple}
                disabled={!open || busy || readOnly}
                onChange={(event) =>
                  changeDraft(originalStatus, field, event.target.value)
                }
              />
            );
          })}
        <Alert severity={invalidPackageMultiple
          ? "error"
          : validGroup(groupValues, amount, packageSize) ? "success" : "info"}>
          Asignadas:{" "}
          {[groupValues.packages, groupValues.loose, groupValues.scrap].every(
            Number.isFinite,
          )
            ? assigned
            : "—"}{" "}
          de {amount}
          {invalidPackageMultiple && (
            <>. Las unidades en paquetes deben ser múltiplo de {packageSize}.</>
          )}
        </Alert>
      </Stack>
    );
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "fractionation-editor-title",
        sx: {
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          maxHeight: "90dvh",
          p: 2.25,
        },
      }}
    >
      <Stack spacing={1} sx={{ width: "100%", maxWidth: 680, mx: "auto" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <Typography
            id="fractionation-editor-title"
            variant="h6"
            fontWeight={700}
          >
            F-{bundle.lote_externo}
          </Typography>
          <Chip
            size="small"
            label={readOnly ? "Completo" : taken ? "En trabajo" : "Pendiente"}
            color={readOnly ? "success" : taken ? "info" : "warning"}
          />
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={typographyStyles.monospace}
        >
          {bundle.article.code} · {bundle.article.description}
        </Typography>
        <Typography variant="body2">
          Lote {bundle.lote || bundle.lote_externo} · Ubicación de ingreso{" "}
          {bundle.location?.code || "Sin ubicación"}
        </Typography>
        <Typography
          fontWeight={700}
        >
          Ubicación de destino
        </Typography>
        <Stack spacing={1.5}>
          <TextField select fullWidth required label="Depósito"
            value={storeId ?? ""} disabled={!open || busy || readOnly}
            onChange={(event) => {
              setStoreId(Number(event.target.value));
              setParentLocationId(null);
              setSubLocationId(null);
            }}>
            {stores.filter((store) => store.is_active || (readOnly && store.id === storeId)).map((store) => (
              <MenuItem key={store.id} value={store.id}>{store.code} · {store.description}</MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={1.5}>
            <TextField select fullWidth required label="Ubicación"
              sx={{ minWidth: 0, flex: 1 }}
              value={parentLocationId ?? ""} disabled={!open || busy || readOnly || !storeId}
              onChange={(event) => {
                setParentLocationId(Number(event.target.value));
                setSubLocationId(null);
              }}>
              {parentLocations.map((location) => (
                <MenuItem key={location.id} value={location.id}>{location.code}</MenuItem>
              ))}
            </TextField>
            <TextField select fullWidth required={availableSubLocations.length > 0}
              label="Sububicación" value={subLocationId ?? ""}
              sx={{ minWidth: 0, flex: 1 }}
              disabled={!open || busy || readOnly || availableSubLocations.length === 0}
              onChange={(event) => setSubLocationId(Number(event.target.value))}>
              {availableSubLocations.map((location) => (
                <MenuItem key={location.id} value={location.id}>{location.code}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
        {renderEditorGroup("available")}
        {bundle.not_available && renderEditorGroup("not_available")}
        <Alert severity="info">
          {readOnly
            ? "Fardo completado. Solo lectura."
            : "Al guardar, el fardo quedará completo y no podrá modificarse."}
        </Alert>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            fullWidth
            disabled={!open || busy}
            onClick={() => {
              if (open && !busy) onCancel();
            }}
          >
            {readOnly ? "Cerrar" : "Cancelar"}
          </Button>
          <Button
            variant="contained"
            fullWidth
            disabled={!open || readOnly || !valid || busy}
            onClick={() => {
              if (open && !readOnly && valid && !busy) onSave(values);
            }}
          >
            Guardar fardo
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
